/**
 * DynamicTable V2 — top-level orchestrator: validate → normalize schema, manage state, delegate rendering.
 * Modular decomposition: hooks handle domain logic, engine components handle presentation.
 */

import React, {
  Component,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

import { DataTable } from '@packages/ui/patterns/DataTable';
import { EmptyState } from '@packages/ui/layout/EmptyState';
import { toast as toastify } from 'react-toastify';
import { Toast } from '@packages/ui/composites/Toast';
import { Button } from '@packages/ui/primitives/Button';
import { Pagination } from '@packages/ui/patterns/Pagination';
import { useMediaQuery } from '@packages/ui/internals';
import { TABLE_SCROLL_FOOTER_STYLE } from './table-compat';

import { createDefaultRegistry } from './core/plugins/built-in';
import { normalizeSchema } from './core/schema/normalizer';
import { isMultiSectionSchema } from './core/schema/version';
import { SchemaValidationError, validateExternalSchema } from './core/schema/validator';
import type {
  ExternalTableSchema,
  InternalTableSchema,
  InternalActionDef,
  FilterDef,
} from './core/schema/types';

import { HeaderRenderer } from './core/engine/HeaderRenderer';
import { useVirtualRows } from './core/engine/useVirtualRows';
import type { GroupedRow } from './core/engine/useGroupedData';
import { useActionExecutor } from './hooks/useActionExecutor';
import type { ActionExecutionContext } from './registry/ActionRegistry';
import { ActionRegistry } from './registry/ActionRegistry';
import type { PendingConfirmation } from './hooks/useActionExecutor';

import { useTableState } from './core/state/useTableState';
import { useDataInjectionV2 } from './hooks/useDataInjectionV2';
import type { DataInjection } from './hooks/useDataInjectionV2';
import { useComputeRulesV2 } from './hooks/useComputeRulesV2';
import { useRemoteDataV2, hasActiveQuery } from './hooks/useRemoteDataV2';
import type { FetchParams } from './hooks/useRemoteDataV2';
import { useRandomAccessData } from './core/engine/useRandomAccessData';
import TableImportBlock from './components/ImportBlock';
import { useColumnPersistence } from './hooks/useColumnPersistence';
import {
  V2TableInternalContext,
  type V2TableInternalContextValue,
} from './context/V2TableInternalContext';
import type { DynamicTableV2Props, DynamicTableV2Handle } from './core/types/DynamicTableV2.types';
import { mergePluginRegistry } from './core/plugins/mergeRegistry';
import { PluginRegistryMap } from './core/plugins/PluginRegistryMap';
import { EMPTY_SCHEMA } from './core/schema/emptySchema';
import { compileRowStyleRules } from './core/schema/rowStyleCompiler';
import { extractActionIds } from './utils/extractActionIds';
import { clearPendingTableRefresh, consumePendingTableRefresh } from './utils/refreshTable';
import { TableLoadingSkeleton } from './core/engine/TableLoadingSkeleton';
import { V1RendererBridge } from './core/engine/V1RendererBridge';
import { MultiSectionRenderer } from './core/engine/MultiSectionRenderer';
import { TableToolbar } from './core/engine/TableToolbar';
import { FilterChips } from './core/engine/FilterChips';
import { TableDialogs } from './core/engine/TableDialogs';
import { TableBulkToolbar } from './core/engine/TableBulkToolbar';
import { TableBodyRenderer } from './core/engine/TableBodyRenderer';
import { FooterRenderer } from './core/engine/FooterRenderer';
import { PaginationRenderer } from './core/engine/PaginationRenderer';

import { useTableSelection } from './hooks/useTableSelection';
import { useTableFilters } from './hooks/useTableFilters';
import { useTablePagination } from './hooks/useTablePagination';
import { useTableEditing } from './hooks/useTableEditing';
import { useTableColumns } from './hooks/useTableColumns';
import { useTableDataPipeline } from './hooks/useTableDataPipeline';
import { useTableSave } from './hooks/useTableSave';
import { useTableActions } from './hooks/useTableActions';
import { TABLE_IDS } from './core/semantic-contract';

// ---------------------------------------------------------------------------
// Props & imperative handle (extracted to core/types/DynamicTableV2.types.ts)
// ---------------------------------------------------------------------------
export type { DynamicTableV2Props, DynamicTableV2Handle } from './core/types/DynamicTableV2.types';

// ---------------------------------------------------------------------------
// Error boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class DynamicTableV2ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[DynamicTableV2] render error', error, info.componentStack);
    }
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <div className='flex justify-center p-8' role='alert' data-testid='dt-v2-error-boundary'>
          <EmptyState
            size='md'
            icon='emergency_home'
            title={`Table failed to render: ${this.state.error.message}`}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Inner implementation
// ---------------------------------------------------------------------------

const DynamicTableV2Inner = forwardRef<DynamicTableV2Handle, DynamicTableV2Props>(
  function DynamicTableV2Inner(props, ref) {
    const {
      schema: externalSchema,
      data,
      loading = 'idle',
      totalCount = 0,
      onSave,
      isSaveLoading,
      patchEditedRow,
      validateEditedRow,
      selectedKeys: controlledSelectedKeys,
      onSelectionChange,
      onActionIntercept,
      confirmRenderer,
      axiosInstance,
      navigate: navigateProp,
      pluginRegistry: customRegistry,
      checkPermission,
      tableInfo,
      setTableInfo,
      filter,
      onFilterChange,
      onSearchChange,
      containerClassName,
      selectedColumnList,
      onColumnPreferenceChange,
      tablePreferenceLoading,
      onLoadMore,
      loadMoreStatus,
      onServerErrors,
      initialFilters: initialFiltersProp,
      initialSearchText,
      onShowPopover,
      envTokens,
      onBackgroundJobSubmitted,
      footerData,
      onEditStateChange,
      onTotalCountChange,
      isToolbarButtonDisabled,
      renderToolbarSuffix,
      onSelectionMetaChange,
      namespace: namespaceProp,
    } = props;

    const namespace = namespaceProp ?? externalSchema.metadata?.entity ?? 'default';

    // ---- Mobile detection for responsive bulk-action overflow menu
    const isMobile = useMediaQuery('(max-width: 639px)');

    // ---- Refresh generation for imperative refreshData
    const refreshGenerationRef = useRef(0);
    const [refreshGeneration, setRefreshGeneration] = useState(0);

    // ---- Last action row (exposed via getActionRow() on the ref handle)
    const actionRowRef = useRef<Record<string, unknown> | null>(null);

    // ---- Plugin registry
    const registry = useMemo(() => {
      const base = createDefaultRegistry();
      if (externalSchema.plugins) {
        const schemaRegistry = PluginRegistryMap.get(externalSchema.plugins);
        if (schemaRegistry) mergePluginRegistry(base, schemaRegistry);
      }
      if (customRegistry) mergePluginRegistry(base, customRegistry);
      return base;
    }, [customRegistry, externalSchema.plugins]);

    // ---- Schema normalization
    const [internalSchema, schemaError] = useMemo((): [InternalTableSchema, Error | null] => {
      try {
        const validation = validateExternalSchema(externalSchema);
        if (validation.errors.length > 0) {
          const criticals = validation.errors.filter((e) => e.severity === 'critical');
          if (criticals.length > 0) {
            try {
              return [
                normalizeSchema(externalSchema, registry),
                new SchemaValidationError(criticals),
              ];
            } catch {
              return [EMPTY_SCHEMA, new SchemaValidationError(criticals)];
            }
          }
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('[DynamicTableV2] Schema issues:', validation.errors);
          }
        }
        if (process.env.NODE_ENV === 'development' && validation.warnings.length > 0) {
          // eslint-disable-next-line no-console
          console.warn('[DynamicTableV2] Schema warnings:', validation.warnings);
        }
        return [normalizeSchema(externalSchema, registry), null];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[DynamicTableV2] schema normalization failed:', err);
        return [EMPTY_SCHEMA, err instanceof Error ? err : new Error(String(err))];
      }
    }, [externalSchema, registry]);

    // ---- Resolve row style function (declarative rules > imperative styleFn > deprecated rowStyle)
    const resolvedRowStyleFn = useMemo(() => {
      const rows = externalSchema.features?.rows;
      if (rows?.styleRules && rows.styleRules.length > 0) {
        return compileRowStyleRules(rows.styleRules);
      }
      if (rows?.styleFn) return rows.styleFn;
      return externalSchema.rowStyle;
    }, [externalSchema.features?.rows, externalSchema.rowStyle]);

    // ---- Auto-load action domain modules for actions referenced in schema
    useEffect(() => {
      const ids = extractActionIds(internalSchema);
      if (ids.length > 0) {
        void ActionRegistry.ensureActions(ids);
      }
    }, [internalSchema]);

    // ---- Central state via useTableState
    const {
      state: tableState,
      actions: stateActions,
      computed,
    } = useTableState({
      defaultPageSize: internalSchema.features.pagination.defaultSize,
      defaultSort: null,
      initialData: data ?? [],
    });

    // ---- Shared search/filter injection from MultiSectionRenderer parent
    const sharedSearchText = props._sharedSearchText;
    const sharedQueryParams = props._sharedQueryParams;

    useEffect(() => {
      if (sharedSearchText !== undefined) {
        stateActions.setSearch(sharedSearchText);
      }
    }, [sharedSearchText, stateActions]);

    useEffect(() => {
      if (sharedQueryParams) {
        Object.entries(sharedQueryParams).forEach(([key, value]) => {
          stateActions.setFilter(key, value);
        });
      }
    }, [sharedQueryParams, stateActions]);

    // ---- Remote data fetching (schema-driven, only when no external `data` prop)
    const externalDataProvided = data != null;
    const paginationMode = internalSchema.features.pagination.mode;
    const vsConfig = internalSchema.features.virtualScroll;
    const isRandomAccessMode =
      !externalDataProvided &&
      vsConfig.enabled &&
      paginationMode === 'infiniteScroll';

    const fetchParams: FetchParams = {
      searchText: tableState.searchText,
      filters: tableState.filters,
      sort: tableState.sort,
      pageIndex: tableState.pagination.pageIndex,
      pageSize: tableState.pagination.pageSize,
    };

    const remoteData = useRemoteDataV2({
      dataSource: internalSchema.dataSource,
      fetchParams,
      axiosInstance,
      refreshGeneration,
      externalDataProvided: externalDataProvided || isRandomAccessMode,
      paginationMode,
    });

    // ── Random-access virtual scroll data layer ─────────────────────────
    const randomAccessData = useRandomAccessData({
      dataSource: isRandomAccessMode ? internalSchema.dataSource : null,
      fetchParams,
      axiosInstance,
      refreshGeneration,
      externalDataProvided: !isRandomAccessMode,
      pageSize: vsConfig.pageSize,
      maxCachedPages: vsConfig.maxCachedPages,
      fetchDebounce: vsConfig.fetchDebounce,
      namespace,
    });

    // ── Data sync into tableState (for editing/save pipeline) ───────────
    const isAccumulationMode = paginationMode === 'loadMore' || paginationMode === 'infiniteScroll';
    const lastSyncedDataRef = useRef<unknown[]>([]);
    useEffect(() => {
      if (!externalDataProvided && !isRandomAccessMode && remoteData.data !== lastSyncedDataRef.current) {
        lastSyncedDataRef.current = remoteData.data;
        if (isAccumulationMode) {
          stateActions.accumulateData(remoteData.data);
        } else {
          stateActions.setData(remoteData.data);
        }
      }
    }, [externalDataProvided, isRandomAccessMode, isAccumulationMode, remoteData.data, stateActions, tableState.selectedKeys.length]);

    useEffect(() => {
      if (data) stateActions.setData(data);
    }, [data, stateActions]);

    // Sync server errors
    useEffect(() => {
      if (onServerErrors) {
        stateActions.setServerErrors(onServerErrors);
      }
    }, [onServerErrors, stateActions]);

    // ---- Selection config
    const selection = internalSchema.features.selection;
    const showSelectionColumn = selection.enabled;
    const keyField = selection.keyField || 'id';

    // ---- Toast
    const showToast = useCallback((message: string, type: 'success' | 'error') => {
      if (type === 'success') {
        toastify.success(<Toast label='Success' message={message} />);
      } else {
        toastify.error(<Toast label='Error' message={message} />);
      }
    }, []);

    // ---- Confirmation dialog state (actions)
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirmation | null>(null);

    // ---- Background-job modal state
    const [pendingBackgroundJob, setPendingBackgroundJob] = useState<any>(null);

    // ---- Toolbar action loading (tracks which buttons are currently executing)
    const [executingActionIds, setExecutingActionIds] = useState<Set<string>>(new Set());

    // ---- Schema-driven column persistence
    const {
      selectedColumns: persistedColumns,
      saveColumns: persistColumns,
      loading: columnPrefLoading,
    } = useColumnPersistence({
      enabled: internalSchema.features.columnPreferences != null,
      tableKey: internalSchema.features.columnPreferences?.persistenceKey ?? '',
      axiosInstance,
    });

    const effectiveSelectedColumnList =
      internalSchema.features.columnPreferences != null ? persistedColumns : selectedColumnList;
    const effectiveOnColumnChange =
      internalSchema.features.columnPreferences != null ? persistColumns : onColumnPreferenceChange;
    const effectiveColumnPrefLoading = columnPrefLoading || (tablePreferenceLoading ?? false);

    // ---- Action executor
    const defaultNavigate = useCallback(
      (path: string, options?: { state?: Record<string, unknown> }) => {
        if (navigateProp) {
          navigateProp(path, options);
        } else {
          window.location.href = path;
        }
      },
      [navigateProp]
    );

    // ---- Display data
    const displayData = externalDataProvided
      ? (data ?? [])
      : isRandomAccessMode
        ? randomAccessData.cachedRows
        : remoteData.data;

    // ---- Resolve row key
    const indexFallbackWarned = useRef(false);
    const resolveRowKey = useCallback(
      (row: Record<string, unknown>, rowIndex: number): string => {
        const raw = row[keyField];
        if (raw != null && raw !== '') return String(raw);
        if (process.env.NODE_ENV !== 'production' && !indexFallbackWarned.current) {
          indexFallbackWarned.current = true;
          console.warn(
            `[DynamicTableV2] Row at index ${rowIndex} has no value for keyField "${keyField}". Falling back to index-based key.`
          );
        }
        return `__idx_${rowIndex}`;
      },
      [keyField]
    );

    const resolveReactKey = useCallback(
      (row: Record<string, unknown>, rowIndex: number): string => {
        const raw = row[keyField];
        if (raw != null && raw !== '') return `${String(raw)}_${rowIndex}`;
        return `__idx_${rowIndex}`;
      },
      [keyField]
    );

    // ---- Compute rules
    const { applyComputeRules } = useComputeRulesV2(internalSchema.computeRules);

    // ════════════════════════════════════════════════════════════════════════
    // PHASE 1 HOOKS — wired from hooks/
    // ════════════════════════════════════════════════════════════════════════

    // ---- Columns (Phase 1)
    const columns = useTableColumns({
      internalSchema,
      effectiveSelectedColumnList,
      tableState: {
        columnOrder: tableState.columnOrder,
        columnSizes: tableState.columnSizes,
      },
      stateActions: {
        setColumnOrder: stateActions.setColumnOrder,
        setColumnSizes: stateActions.setColumnSizes,
        resizeColumn: stateActions.resizeColumn,
      },
      showSelectionColumn,
    });

    // ---- Data pipeline (Phase 2)
    const pipeline = useTableDataPipeline({
      displayData,
      tableState: {
        sort: tableState.sort,
        pagination: tableState.pagination,
        expandedGroups: tableState.expandedGroups,
      },
      internalSchema,
      resolveRowKey,
      visibleColumns: columns.visibleColumns,
    });

    // ---- Selection (Phase 1)
    const selectionHook = useTableSelection({
      internalSchema,
      displayData,
      paginatedData: pipeline.paginatedData,
      controlledSelectedKeys,
      onSelectionChange,
      stateActions: {
        selectRow: stateActions.selectRow,
        deselectAll: stateActions.deselectAll,
      },
      tableStateSelectedKeys: tableState.selectedKeys,
      resolveRowKey,
    });

    // ---- Editing (Phase 1)
    const effectivePatchEditedRow = patchEditedRow ?? internalSchema.features.editing.patchEditedRow ?? undefined;
    const effectiveValidateEditedRow = validateEditedRow ?? internalSchema.features.editing.validateEditedRow ?? undefined;

    const editing = useTableEditing({
      internalSchema,
      tableState: {
        editingRows: tableState.editingRows,
        editErrors: tableState.editErrors,
        serverErrors: tableState.serverErrors,
      },
      stateActions: {
        startEditing: stateActions.startEditing,
        startEditingBatch: stateActions.startEditingBatch,
        updateEditValue: stateActions.updateEditValue,
        cancelEditing: stateActions.cancelEditing,
        cancelAllEditing: stateActions.cancelAllEditing,
        setRowEditErrors: stateActions.setRowEditErrors,
      },
      displayData,
      resolveRowKey,
      applyComputeRules,
      patchEditedRow: effectivePatchEditedRow,
      validateEditedRow: effectiveValidateEditedRow,
    });

    // ---- Filters (Phase 1)
    const filters = useTableFilters({
      filterDefs: internalSchema.dataSource?.filters ?? [],
      tableStateFilters: tableState.filters,
      stateActions: {
        setFilter: stateActions.setFilter,
        setFilters: stateActions.setFilters,
        setSearch: stateActions.setSearch,
      },
      onFilterChange,
      onSearchChange,
      setTableInfo,
      tableInfo,
      initialFiltersProp,
      initialSearchText,
      searchDebounce: internalSchema.features.search.debounce,
    });

    // ---- Pagination (Phase 1)
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useTablePagination({
      paginationMode,
      pageSize: tableState.pagination.pageSize,
      stateActions: {
        setPage: stateActions.setPage,
        setPageSize: stateActions.setPageSize,
      },
      setTableInfo,
      tableInfo,
      onLoadMore,
      loadMoreStatus,
      externalDataProvided,
      remoteHasMore: remoteData.hasMore,
      remoteLoadMore: remoteData.loadMore,
      remoteIsLoadingMore: remoteData.isLoadingMore,
      scrollContainerRef,
    });

    // ---- Save (Phase 2)
    const { executeAction, executeSave: executeSaveAction } = useActionExecutor({
      axiosInstance,
      navigate: defaultNavigate,
      onRefetch: () => {
        refreshGenerationRef.current += 1;
        setRefreshGeneration(refreshGenerationRef.current);
        if (!isRandomAccessMode && !externalDataProvided) {
          remoteData.refetch();
        }
      },
      onRemoveRow: (rowId: string) => {
        const filtered = tableState.data.filter((r) => {
          const rec = r as Record<string, unknown>;
          return String(rec[keyField] ?? '') !== rowId;
        });
        stateActions.setData(filtered);
      },
      onToast: showToast,
      onClearSelection: () => selectionHook.handleSelectionChange([]),
      onBackgroundJob: onBackgroundJobSubmitted
        ? (result, action, context) => {
            onBackgroundJobSubmitted(result, action, context);
          }
        : (result) => {
            setPendingBackgroundJob(result);
          },
      onActionIntercept: async (action, context) => {
        if (context.row) {
          actionRowRef.current = context.row;
        }
        if (onActionIntercept) {
          return Promise.resolve(onActionIntercept(action, context));
        }
        return true;
      },
      rowIdField: internalSchema.dataSource?.rowIdField ?? keyField,
      envTokens,
      onStartEditing: (_rowKey, row) => {
        const idx = displayData.indexOf(row as unknown);
        const resolvedKey = idx >= 0 ? resolveRowKey(row, idx) : resolveRowKey(row, 0);
        if (!tableState.editingRows.has(resolvedKey)) {
          const entries = editing.buildWholeRowEditEntries(resolvedKey, row);
          stateActions.startEditingBatch(entries);
        }
      },
      onStartEditingAll: () => {
        const allEntries: Array<{ rowKey: string; initialValues: Record<string, unknown> }> = [];
        (displayData as Record<string, unknown>[]).forEach((row, idx) => {
          const rowKey = resolveRowKey(row, idx);
          if (!tableState.editingRows.has(rowKey)) {
            allEntries.push(...editing.buildWholeRowEditEntries(rowKey, row));
          }
        });
        if (allEntries.length > 0) {
          stateActions.startEditingBatch(allEntries);
        }
      },
    });

    const registryRefreshTable = useCallback(() => {
      refreshGenerationRef.current += 1;
      setRefreshGeneration(refreshGenerationRef.current);
      if (!isRandomAccessMode && !externalDataProvided) {
        remoteData.refetch();
      }
    }, [isRandomAccessMode, externalDataProvided, remoteData]);

    useEffect(() => {
      const handler = (e: Event) => {
        const target = (e as CustomEvent).detail?.namespace;
        if (target == null || target === namespace || target === internalSchema.entity) {
          if (typeof target === 'string') {
            clearPendingTableRefresh(target);
          }
          registryRefreshTable();
        }
      };
      window.addEventListener('dt-v2:refresh', handler);
      return () => window.removeEventListener('dt-v2:refresh', handler);
    }, [registryRefreshTable, namespace, internalSchema.entity]);

    useEffect(() => {
      const entityKey = namespace ?? internalSchema.entity;
      if (entityKey && consumePendingTableRefresh(entityKey)) {
        registryRefreshTable();
      }
    }, [registryRefreshTable, namespace, internalSchema.entity]);

    const save = useTableSave({
      internalSchema,
      tableState: {
        editingRows: tableState.editingRows,
        data: tableState.data,
      },
      stateActions: {
        applyEdit: stateActions.applyEdit,
        cancelEditing: stateActions.cancelEditing,
        cancelEditingBatch: stateActions.cancelEditingBatch,
        cancelAllEditing: stateActions.cancelAllEditing,
        clearServerErrors: stateActions.clearServerErrors,
        setServerErrors: stateActions.setServerErrors,
        setRowEditErrors: stateActions.setRowEditErrors,
      },
      displayData,
      resolveRowKey,
      getSubRowKeysForParent: editing.getSubRowKeysForParent,
      onSave,
      validateEditedRow: effectiveValidateEditedRow,
      isSaveLoading,
      executeSaveAction,
    });

    // ---- Sort handler
    const handleSort = useCallback(
      (field: string) => {
        stateActions.toggleSort(field);
        if (setTableInfo && tableInfo) {
          const currentSort = tableState.sort;
          let newSorting = '';
          if (currentSort?.field === field) {
            newSorting = currentSort.direction === 'asc' ? `${field} desc` : '';
          } else {
            newSorting = `${field} asc`;
          }
          setTableInfo({ ...tableInfo, sorting: newSorting, skipCount: 0 });
        }
      },
      [stateActions, setTableInfo, tableInfo, tableState.sort]
    );

    // ---- Data injection
    const dataInjections: DataInjection[] = useMemo(() => {
      return internalSchema.dataInjections.map((di) => ({
        id: di.id,
        url: di.url,
        method: di.method,
        targetField: di.targetField,
        mapper: { label: di.mapper.label, value: di.mapper.value },
        body: di.body ? { ...di.body } : null,
        cacheDuration: di.cacheDuration,
      }));
    }, [internalSchema.dataInjections]);

    const subRowDataInjections: DataInjection[] = useMemo(() => {
      return internalSchema.subRowDataInjections.map((di) => ({
        id: di.id,
        url: di.url,
        method: di.method,
        targetField: di.targetField,
        mapper: { label: di.mapper.label, value: di.mapper.value },
        body: di.body ? { ...di.body } : null,
        cacheDuration: di.cacheDuration,
      }));
    }, [internalSchema.subRowDataInjections]);

    const { injectedData, subRowInjectedData } = useDataInjectionV2(
      dataInjections,
      subRowDataInjections,
      undefined,
      axiosInstance
    );

    // ---- Build table query for selectAll actions
    const buildTableQuery = useCallback((): Record<string, unknown> => {
      const query: Record<string, unknown> = {
        ...(internalSchema.dataSource?.staticQueryParams ?? {}),
      };
      if (internalSchema.dataSource) {
        for (const [paramKey, token] of Object.entries(internalSchema.dataSource.requestParams)) {
          switch (token) {
            case 'table.searchText':
              if (tableState.searchText) query[paramKey] = tableState.searchText;
              break;
            case 'table.pageSize':
              query[paramKey] = tableState.pagination.pageSize;
              break;
            case 'table.pageOffset':
              query[paramKey] = tableState.pagination.pageIndex * tableState.pagination.pageSize;
              break;
            case 'table.sorting':
              if (tableState.sort)
                query[paramKey] = `${tableState.sort.field} ${tableState.sort.direction}`;
              break;
          }
        }
      }
      for (const [filterName, filterValue] of Object.entries(tableState.filters)) {
        if (filterValue !== undefined && filterValue !== null && filterValue !== '') {
          query[filterName] = filterValue;
        }
      }
      return query;
    }, [
      internalSchema.dataSource,
      tableState.searchText,
      tableState.filters,
      tableState.pagination,
      tableState.sort,
    ]);

    // ---- Legacy import file handler
    const handleLegacyImportFile = useCallback(async (
      a: InternalActionDef,
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const formData = new FormData();
      formData.append(a.fileKey ?? 'File', file);
      const path = (a.endpoint ?? '').replace(/^\//, '');
      try {
        if (axiosInstance) {
          await axiosInstance.post(path, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          const token = localStorage.getItem('access_token');
          const baseUrl = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
          const url = a.endpoint?.startsWith('http') ? a.endpoint : `${baseUrl}/${path}`;
          const headers: Record<string, string> = {};
          if (token) headers.Authorization = `Bearer ${token}`;
          const resp = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
          });
          if (!resp.ok) throw new Error(`Import failed: ${resp.status}`);
        }
        showToast(a.successMessage ?? `${a.label} completed`, 'success');
        if (a.onSuccess === 'refetch' || a.onSuccess === 'toastAndRefetch') {
          refreshGenerationRef.current += 1;
          setRefreshGeneration(refreshGenerationRef.current);
          if (!externalDataProvided) remoteData.refetch();
        }
      } catch (err) {
        showToast(a.errorMessage ?? `Failed to ${a.label.toLowerCase()}`, 'error');
      }
    }, [axiosInstance, showToast, externalDataProvided, remoteData]);

    // ---- Actions (Phase 2)
    const effectiveTotalCount = isRandomAccessMode
      ? randomAccessData.totalRecords
      : externalDataProvided
        ? totalCount || displayData.length
        : remoteData.totalCount || displayData.length;

    useEffect(() => {
      onTotalCountChange?.(effectiveTotalCount);
    }, [effectiveTotalCount, onTotalCountChange]);

    useEffect(() => {
      onSelectionMetaChange?.({
        selectedKeys: selectionHook.selectedKeysValue,
        isAllPagesSelected: selectionHook.isAllPagesSelected,
        uncheckedIds: selectionHook.uncheckedIds,
        totalCount: effectiveTotalCount,
      });
    }, [
      onSelectionMetaChange,
      selectionHook.selectedKeysValue,
      selectionHook.isAllPagesSelected,
      selectionHook.uncheckedIds,
      effectiveTotalCount,
    ]);

    const actions = useTableActions({
      internalSchema,
      selectedKeysValue: selectionHook.selectedKeysValue,
      isAllPagesSelected: selectionHook.isAllPagesSelected,
      uncheckedIds: selectionHook.uncheckedIds,
      effectiveTotalCount,
      buildTableQuery,
      executeAction,
      setPendingConfirm,
      registryRefreshTable,
      axiosInstance,
      handleLegacyImportFile,
    });

    // ---- Sub-row expand handler
    const subRowsSchema = internalSchema.features.rows.subRows;
    const subRowExpandMode = subRowsSchema?.expandMode ?? 'alwaysOpen';

    const subRowExpansionInitRef = useRef<string | null>(null);
    useEffect(() => {
      if (!subRowsSchema) return;
      if (subRowExpandMode !== 'accordion') return;
      const dataField = subRowsSchema.dataField;
      const firstRowKey = pipeline.paginatedData
        .map((r, i) => {
          const rec = r as Record<string, unknown>;
          if (Array.isArray(rec[dataField]) && (rec[dataField] as unknown[]).length > 0) {
            return resolveRowKey(rec, i);
          }
          return null;
        })
        .find(Boolean);
      if (!firstRowKey) return;
      if (subRowExpansionInitRef.current === firstRowKey) return;
      subRowExpansionInitRef.current = firstRowKey;
      stateActions.expandAll([firstRowKey]);
    }, [subRowsSchema, subRowExpandMode, pipeline.paginatedData, resolveRowKey, stateActions]);

    const subRowExpandAllInitRef = useRef(false);
    useEffect(() => {
      if (!subRowsSchema) return;
      if (subRowExpandMode !== 'expanded') return;
      if (subRowExpandAllInitRef.current) return;
      subRowExpandAllInitRef.current = true;
      const dataField = subRowsSchema.dataField;
      const keysToExpand = pipeline.paginatedData
        .map((r, i) => {
          const rec = r as Record<string, unknown>;
          if (Array.isArray(rec[dataField]) && (rec[dataField] as unknown[]).length > 0) {
            return resolveRowKey(rec, i);
          }
          return null;
        })
        .filter(Boolean) as string[];
      if (keysToExpand.length > 0) {
        stateActions.expandAll(keysToExpand);
      }
    }, [subRowsSchema, subRowExpandMode, pipeline.paginatedData, resolveRowKey, stateActions]);

    const handleToggleRowExpand = useCallback(
      (rowKey: string) => {
        if (subRowExpandMode === 'accordion') {
          if (tableState.expandedRows.has(rowKey)) {
            stateActions.collapseAll();
          } else {
            stateActions.collapseAll();
            stateActions.toggleExpand(rowKey);
          }
        } else {
          stateActions.toggleExpand(rowKey);
        }
      },
      [subRowExpandMode, tableState.expandedRows, stateActions]
    );

    // ---- Group expansion init
    const groupExpansionInitializedRef = useRef(false);
    useEffect(() => {
      if (!pipeline.isGroupingEnabled) {
        groupExpansionInitializedRef.current = false;
        return;
      }
      if (groupExpansionInitializedRef.current) return;
      groupExpansionInitializedRef.current = true;
      if (pipeline.groupExpandMode === 'expanded' || pipeline.groupAlwaysExpanded) {
        stateActions.expandAllGroups(pipeline.groupKeys);
      } else {
        stateActions.collapseAllGroups();
      }
    }, [pipeline.isGroupingEnabled, pipeline.groupExpandMode, pipeline.groupAlwaysExpanded, pipeline.groupKeys, stateActions]);

    const allGroupsExpanded = useMemo(() => {
      if (!pipeline.isGroupingEnabled || pipeline.groupKeys.length === 0) return false;
      return pipeline.groupKeys.every((k) => tableState.expandedGroups.has(k));
    }, [pipeline.isGroupingEnabled, pipeline.groupKeys, tableState.expandedGroups]);

    // ---- Derived values
    const hasRowActions = internalSchema.actions.row.length > 0;
    const hasEditableColumns = internalSchema.columns.some((c) => c.editable !== null);
    const subRowConfig = internalSchema.features.rows.subRows;
    const subRowColumns = internalSchema.subRowColumns;

    const effectiveSubRowActions = useMemo(() => {
      if (internalSchema.actions.subRow.length > 0) return internalSchema.actions.subRow;
      return subRowConfig?.actions ?? [];
    }, [internalSchema.actions.subRow, subRowConfig?.actions]);

    const externalEditControl = internalSchema.features.editing.externalControl;
    const showActionsColumn =
      hasRowActions || (hasEditableColumns && !externalEditControl) || effectiveSubRowActions.length > 0;

    // When sub-rows define more data columns than the parent, extra invisible
    // <col> entries and filler <td> cells are added so parent and sub-row <tr>
    // elements have matching cell counts.
    const subRowOverflowCols = useMemo(() => {
      if (subRowColumns.length === 0) return 0;
      const indentSlot = subRowConfig?.indent !== false ? 1 : 0;
      const actionSlot = showActionsColumn ? 1 : 0;
      const parentDataSlots =
        (showSelectionColumn ? 1 : 0) + columns.visibleColumns.length + (showActionsColumn ? 1 : 0) -
        indentSlot - actionSlot;
      return Math.max(0, subRowColumns.length - parentDataSlots);
    }, [subRowColumns.length, subRowConfig?.indent, showActionsColumn, showSelectionColumn, columns.visibleColumns.length]);

    const paginationConfig = internalSchema.features.pagination;
    const virtualScrollConfig = internalSchema.features.virtualScroll;

    // ---- Virtual scroll
    const VIRTUAL_THRESHOLD = virtualScrollConfig.threshold;
    const shouldVirtualize = isRandomAccessMode
      ? virtualScrollConfig.enabled && randomAccessData.totalRecords > 0
      : virtualScrollConfig.enabled !== false && pipeline.renderData.length > VIRTUAL_THRESHOLD;

    const virtualRows = useVirtualRows(
      isRandomAccessMode
        ? {
            scrollContainerRef: scrollContainerRef as React.RefObject<HTMLElement | null>,
            totalRecords: randomAccessData.totalRecords,
            getRow: randomAccessData.getRow,
            onVisibleRangeChange: randomAccessData.requestPages,
            enabled: shouldVirtualize,
            estimatedRowHeight: virtualScrollConfig.estimatedRowHeight,
            overscan: virtualScrollConfig.overscan,
            expandedRows: tableState.expandedRows,
            estimatedSubRowHeight: 47,
            subRowDataField: subRowConfig?.dataField,
            rowIdField: keyField,
          }
        : {
            scrollContainerRef: scrollContainerRef as React.RefObject<HTMLElement | null>,
            renderData: pipeline.renderData as ReadonlyArray<Record<string, unknown> | GroupedRow>,
            enabled: shouldVirtualize,
            estimatedRowHeight: virtualScrollConfig.estimatedRowHeight,
            overscan: virtualScrollConfig.overscan,
            expandedRows: tableState.expandedRows,
            estimatedSubRowHeight: 47,
            subRowDataField: subRowConfig?.dataField,
            rowIdField: keyField,
          },
    );

    // ---- Effective loading state
    const effectiveLoading = isRandomAccessMode
      ? (randomAccessData.status === 'initializing' || randomAccessData.status === 'transitioning')
        ? 'loading'
        : loading
      : externalDataProvided
        ? loading
        : remoteData.loading === 'loading' && !remoteData.isLoadingMore
          ? 'loading'
          : remoteData.loading === 'error'
            ? 'error'
            : loading;

    // ---- Column picker
    const optionalColumns = useMemo(() => {
      return internalSchema.columns.filter((col) => col.visibility === 'picker');
    }, [internalSchema.columns]);

    const handleColumnToggle = useCallback(
      (field: string) => {
        if (!effectiveOnColumnChange) return;
        const current = effectiveSelectedColumnList ?? [];
        const next = current.includes(field)
          ? current.filter((f) => f !== field)
          : [...current, field];
        effectiveOnColumnChange(next);
      },
      [effectiveOnColumnChange, effectiveSelectedColumnList]
    );

    // ---- Filter defs
    const filterDefs: readonly FilterDef[] = useMemo(() => {
      return internalSchema.dataSource?.filters ?? [];
    }, [internalSchema.dataSource]);

    // ---- Page handlers (for PaginationRenderer)
    const handlePageChange = useCallback(
      (pageIndex: number) => {
        stateActions.setPage(pageIndex);
        if (setTableInfo && tableInfo) {
          setTableInfo({
            ...tableInfo,
            skipCount: pageIndex * tableState.pagination.pageSize,
          });
        }
      },
      [stateActions, setTableInfo, tableInfo, tableState.pagination.pageSize]
    );

    const handlePageSizeChange = useCallback(
      (pageSize: number) => {
        stateActions.setPageSize(pageSize);
        if (setTableInfo && tableInfo) {
          setTableInfo({ ...tableInfo, skipCount: 0, maxResultCount: pageSize });
        }
      },
      [stateActions, setTableInfo, tableInfo]
    );

    // ---- Imperative handle
    useImperativeHandle(
      ref,
      () => ({
        getSchema: () => internalSchema,
        getSelectedKeys: () => selectionHook.selectedKeysValue,
        resetSelection: () => selectionHook.handleSelectionChange([]),
        refreshData: () => {
          refreshGenerationRef.current += 1;
          setRefreshGeneration(refreshGenerationRef.current);
        },
        getEditedData: () => computed.getEditedValues(),
        cancelAllEditing: () => stateActions.cancelAllEditing(),
        startEditingAll: () => {
          const allEntries: Array<{ rowKey: string; initialValues: Record<string, unknown> }> = [];
          (displayData as Record<string, unknown>[]).forEach((row, idx) => {
            const rowKey = resolveRowKey(row, idx);
            if (!tableState.editingRows.has(rowKey)) {
              allEntries.push(...editing.buildWholeRowEditEntries(rowKey, row));
            }
          });
          if (allEntries.length > 0) stateActions.startEditingBatch(allEntries);
        },
        saveAll: () => save.handleBulkSave(),
        getActionRow: () => actionRowRef.current,
      }),
      [internalSchema, selectionHook, computed, stateActions, displayData, resolveRowKey, tableState.editingRows, editing, save]
    );

    // ---- External edit state change notification
    useEffect(() => {
      onEditStateChange?.(computed.hasEdits);
    }, [computed.hasEdits, onEditStateChange]);

    // ---- V2 internal context (MUST be before the throw — Phase 4 fix)
    const v2InternalCtxValue = useMemo(
      () => ({
        handleShow: onShowPopover,
        axiosInstance: (axiosInstance ?? null) as V2TableInternalContextValue['axiosInstance'],
      }),
      [onShowPopover, axiosInstance]
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // ALL React hooks are above this line.  Nothing below may call useState,
    // useRef, useMemo, useCallback, useEffect, useReducer, or useImperativeHandle.
    // Early returns and throws are safe from here on.
    // ═══════════════════════════════════════════════════════════════════════════

    if (schemaError) throw schemaError;

    if (effectiveLoading === 'error') {
      return (
        <V2TableInternalContext.Provider value={v2InternalCtxValue}>
          <div
            id={TABLE_IDS.container}
            className={containerClassName}
            data-testid='dynamic-table-v2'>
            <DataTable.Root data-testid='dt-v2-error'>
              <div className='flex justify-center p-8' role='alert'>
                <EmptyState size='md' icon='emergency_home' title='Unable to load table data.' />
              </div>
            </DataTable.Root>
          </div>
        </V2TableInternalContext.Provider>
      );
    }

    // ---- Toolbar visibility
    const toolbarVisible =
      internalSchema.features.search.enabled ||
      internalSchema.actions.toolbar.buttons.length > 0 ||
      internalSchema.actions.bulk.length > 0 ||
      filterDefs.length > 0 ||
      internalSchema.features.columnPreferences?.selectorEnabled === true;

    const toolbarButtons = internalSchema.features.editing.externalControl
      ? internalSchema.actions.toolbar.buttons.filter((b) => b.actionType !== 'startEditingAll')
      : internalSchema.actions.toolbar.buttons;
    const bulk = internalSchema.actions.bulk;

    // ---- Table body height — pure CSS, no data-length checks
    const tableBodyHeight = (() => {
      const schemaHeight = internalSchema.features.rows.maxHeight;
      if (schemaHeight != null) return `${schemaHeight}px`;
      return 'calc(100dvh - 280px)';
    })();

    // ---- Scroll footer (load more button / random-access shimmer — inside scroll container)
    const tableScrollFooter = (() => {
      const nodes: React.ReactNode[] = [];
      if (paginationConfig.mode === 'loadMore') {
        const effectiveOnLoadMore =
          onLoadMore ??
          (!externalDataProvided && remoteData.hasMore ? remoteData.loadMore : undefined);
        const isLoadingMoreActive = loadMoreStatus === 'loading' || remoteData.isLoadingMore;
        const showLoadMore = effectiveOnLoadMore && (onLoadMore ? true : remoteData.hasMore);
        if (showLoadMore) {
          nodes.push(
            <div key='load-more' style={TABLE_SCROLL_FOOTER_STYLE} data-testid='dt-v2-load-more-container'>
              <Button
                label='Load More'
                type='button'
                variant='secondary'
                size='sm'
                onClick={effectiveOnLoadMore}
                loading={isLoadingMoreActive}
                disabled={isLoadingMoreActive}
                semanticId={TABLE_IDS.loadMoreButton}
              />
            </div>
          );
        }
      }
      if (isRandomAccessMode && randomAccessData.status === 'fetching') {
        nodes.push(
          <div
            key='random-access-loading'
            style={{ height: 3, background: 'linear-gradient(90deg, transparent, var(--color-primary, #3b82f6), transparent)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite linear' }}
            data-testid='dt-v2-random-access-loading'
          />
        );
      }
      if (nodes.length === 0) return null;
      return <>{nodes}</>;
    })();

    // ---- Infinite scroll status bar (outside scroll container, uses DS Pagination for styling)
    const infiniteScrollStatusBar = (() => {
      if (paginationConfig.mode !== 'infiniteScroll') return null;

      const loadedCount = isRandomAccessMode
        ? randomAccessData.cachedRows.length
        : pipeline.renderData.length;
      const totalCount = effectiveTotalCount;
      if (loadedCount === 0 && totalCount === 0) return null;

      return (
        <Pagination
          totalCount={totalCount}
          currentPage={1}
          pageSize={totalCount}
          totalPages={0}
          visibleCount={loadedCount}
          onPageChange={() => {}}
          semanticId='infiniteScrollPagination'
          data-testid='dt-v2-infinite-scroll-indicator'
          style={{ padding: '8px 20px' }}
        />
      );
    })();

    const liveTableClassName = clsx(
      containerClassName,
      computed.hasEdits &&
        (internalSchema.features.editing.bulkSave || computed.editedRowKeys.length > 1) &&
        'hide-action-menu',
      shouldVirtualize && 'dt-v2-virtualized',
    );

    return (
      <V2TableInternalContext.Provider value={v2InternalCtxValue}>
        <div
          id={TABLE_IDS.container}
          className={containerClassName}
          data-testid='dynamic-table-v2'>
          {/* ---- TOOLBAR ---- */}
          {toolbarVisible && (
            <TableToolbar
              searchEnabled={internalSchema.features.search.enabled}
              searchPlaceholder={internalSchema.features.search.placeholder}
              searchInput={filters.searchInput}
              onSearchChange={filters.handleSearchChange}
              filterDefs={filterDefs}
              isFilterOpen={filters.isFilterOpen}
              setIsFilterOpen={filters.setIsFilterOpen}
              pendingFilter={filters.pendingFilter}
              handleFilterChange={filters.handleFilterChange}
              handleApplyFilter={filters.handleApplyFilter}
              handleResetFilter={() => {
                filters.handleResetFilter();
              }}
              handleAsyncOptionsLoaded={filters.handleAsyncOptionsLoaded}
              axiosInstance={axiosInstance}
              buttons={toolbarButtons}
              selectedKeysValue={selectionHook.selectedKeysValue}
              buildActionContext={actions.buildActionContext}
              executeAction={executeAction}
              setPendingConfirm={setPendingConfirm}
              stateActions={stateActions}
              onFilterChange={onFilterChange}
              setPendingFilter={(f) => {
                /* handled internally by useTableFilters */
              }}
              setFilterChips={filters.setFilterChips}
              refreshTable={registryRefreshTable}
              filterGridColumns={internalSchema.features.filterGrid.columns}
              executingActionIds={executingActionIds}
              setExecutingActionIds={setExecutingActionIds}
              isAllPagesSelected={selectionHook.isAllPagesSelected}
              uncheckedIds={selectionHook.uncheckedIds}
              effectiveTotalCount={effectiveTotalCount}
              displayData={displayData}
              resolveRowKey={resolveRowKey}
              buildTableQuery={buildTableQuery}
              clearSelection={selectionHook.handleClearAllPages}
              isToolbarButtonDisabled={isToolbarButtonDisabled}
              renderToolbarSuffix={renderToolbarSuffix}
            />
          )}

          {/* ---- ACTIVE FILTER CHIPS ---- */}
          <FilterChips
            filterChips={filters.filterChips}
            onRemoveChip={(fieldName) => {
              const remaining = filters.filterChips.filter((c) => c.fieldName !== fieldName);
              filters.setFilterChips(remaining);

              const nextFilters: Record<string, unknown> = {};
              Object.entries(tableState.filters).forEach(([k, v]) => {
                if (k !== fieldName && v !== undefined && v !== null && v !== '') {
                  nextFilters[k] = v;
                }
              });

              stateActions.setFilters(nextFilters);
              onFilterChange?.(nextFilters);
              if (setTableInfo && tableInfo) {
                setTableInfo({
                  ...tableInfo,
                  ...nextFilters,
                  [fieldName]: undefined,
                  skipCount: 0,
                });
              }
            }}
            onClearAll={() => {
              filters.setFilterChips([]);
              stateActions.clearFilters();
              onFilterChange?.({});
            }}
          />

          {/* ---- IMPORT/EXPORT BLOCK ---- */}
          {actions.hasImportExportActions && actions.v2TemplateDatas.length > 0 && (
            <TableImportBlock
              {...({
                id: 'tableImportBlock',
                dropdownEnable: false,
                templateDatas: actions.v2TemplateDatas,
                importDescription: externalSchema.features?.import?.description,
              } as any)}
            />
          )}

          <DataTable.Root semanticId={TABLE_IDS.table}>
            {/* ---- BULK TOOLBAR ---- */}
            <TableBulkToolbar
              isMobile={isMobile}
              internalSchema={internalSchema}
              effectiveSelectedKeys={selectionHook.effectiveSelectedKeys}
              isAllPagesSelected={selectionHook.isAllPagesSelected}
              uncheckedIds={selectionHook.uncheckedIds}
              effectiveTotalCount={effectiveTotalCount}
              showSelectionColumn={showSelectionColumn}
              paginatedData={pipeline.paginatedData}
              displayData={displayData}
              resolveRowKey={resolveRowKey}
              isGroupingEnabled={pipeline.isGroupingEnabled}
              groupKeys={pipeline.groupKeys}
              allGroupsExpanded={allGroupsExpanded}
              computed={computed}
              handleSelectAllPages={selectionHook.handleSelectAllPages}
              handleClearAllPages={selectionHook.handleClearAllPages}
              stateActions={{
                collapseAllGroups: stateActions.collapseAllGroups,
                expandAllGroups: stateActions.expandAllGroups,
                cancelAllEditing: stateActions.cancelAllEditing,
                toggleGroup: stateActions.toggleGroup,
              }}
              bulk={bulk}
              buildTableQuery={buildTableQuery}
              registryRefreshTable={registryRefreshTable}
              axiosInstance={axiosInstance}
              buildActionContext={actions.buildActionContext}
              executeAction={executeAction}
              setPendingConfirm={setPendingConfirm}
              optionalColumns={optionalColumns}
              effectiveSelectedColumnList={effectiveSelectedColumnList}
              effectiveOnColumnChange={effectiveOnColumnChange}
              effectiveColumnPrefLoading={effectiveColumnPrefLoading}
              handleBulkSave={save.handleBulkSave}
              effectiveSaveLoading={save.effectiveSaveLoading}
              executingActionIds={executingActionIds}
              setExecutingActionIds={setExecutingActionIds}
            />

            <DataTable.Content
              ref={scrollContainerRef as any}
              className='height-identifier'
              style={{ overflowX: 'auto', contain: 'layout paint', willChange: 'transform' }}
              height={tableBodyHeight}
              padding={columns.pinOffsets.left.size > 0 || columns.pinOffsets.right.size > 0 || internalSchema.features.layout.noPadding ? '0' : undefined}
              tableClassName={liveTableClassName}
              tableId={TABLE_IDS.table}
              tableStyle={{ tableLayout: 'fixed' }}
              data-testid='dt-v2-table'
              footer={tableScrollFooter}>
              {effectiveLoading === 'loading' || effectiveColumnPrefLoading ? (
                <TableLoadingSkeleton
                  columns={columns.visibleColumns}
                  showCheckbox={showSelectionColumn}
                  showActionsColumn={showActionsColumn}
                />
              ) : (
                <React.Fragment>
                  <HeaderRenderer
                    columns={columns.visibleColumns}
                    sortState={tableState.sort}
                    onSort={handleSort}
                    showCheckbox={showSelectionColumn}
                    onSelectAll={selectionHook.handleSelectAll}
                    isAllSelected={selectionHook.allVisibleSelected}
                    isPartiallySelected={selectionHook.isPartiallySelected}
                    showActionsColumn={showActionsColumn}
                    reorderable={columns.isReorderable}
                    onColumnReorder={columns.handleColumnReorder}
                    isGroupingEnabled={pipeline.isGroupingEnabled}
                    resizable={columns.isResizable}
                    columnSizes={tableState.columnSizes}
                    onColumnResize={columns.handleColumnResize}
                    onColumnResizeEnd={columns.handleColumnResizeEnd}
                    pinOffsets={columns.pinOffsets}
                    subRowOverflowCols={subRowOverflowCols}
                  />
                  <TableBodyRenderer
                    renderData={pipeline.renderData}
                    paginatedData={pipeline.paginatedData}
                    topPinnedRows={pipeline.topPinnedRows}
                    isRandomAccessMode={isRandomAccessMode}
                    randomAccessTotalRecords={isRandomAccessMode ? randomAccessData.totalRecords : 0}
                    isAwaitingFilter={
                      isRandomAccessMode
                        ? internalSchema.dataSource?.fetchPolicy === 'requireFilter' &&
                          !hasActiveQuery(fetchParams)
                        : remoteData.isAwaitingFilter
                    }
                    emptyFilterMessage={internalSchema.features.emptyFilterMessage}
                    bottomPinnedRows={pipeline.bottomPinnedRows}
                    shouldVirtualize={shouldVirtualize}
                    virtualRows={virtualRows}
                    groupByConfig={pipeline.groupByConfig}
                    groupAlwaysExpanded={pipeline.groupAlwaysExpanded}
                    selection={selection}
                    visibleColumns={columns.visibleColumns}
                    registry={registry}
                    namespace={namespace}
                    showSelectionColumn={showSelectionColumn}
                    showActionsColumn={showActionsColumn}
                    effectiveSelectedKeys={selectionHook.effectiveSelectedKeys}
                    internalSchema={internalSchema}
                    isGroupingEnabled={pipeline.isGroupingEnabled}
                    effectiveSubRowActions={effectiveSubRowActions}
                    hasEditableColumns={hasEditableColumns}
                    subRowConfig={subRowConfig}
                    subRowColumns={subRowColumns}
                    tableState={{
                      editingRows: tableState.editingRows,
                      editErrors: tableState.editErrors,
                      serverErrors: tableState.serverErrors,
                      expandedRows: tableState.expandedRows,
                      columnSizes: tableState.columnSizes,
                    }}
                    injectedData={injectedData}
                    subRowInjectedData={subRowInjectedData}
                    keyField={keyField}
                    pinOffsets={columns.pinOffsets}
                    editingSubRows={editing.editingSubRows}
                    subRowEditErrors={editing.subRowEditErrors}
                    effectiveSaveLoading={save.effectiveSaveLoading}
                    resolveRowKey={resolveRowKey}
                    resolveReactKey={resolveReactKey}
                    handleRowSelect={selectionHook.handleRowSelect}
                    handleRowActionExec={actions.handleRowActionExec}
                    checkPermission={checkPermission}
                    handleCellDoubleClick={hasEditableColumns ? editing.handleCellDoubleClick : undefined}
                    handleEditChange={editing.handleEditChange}
                    handleEditBlur={editing.handleEditBlur}
                    handleSubRowCellDoubleClick={editing.handleSubRowCellDoubleClick}
                    handleSubRowEditChange={editing.handleSubRowEditChange}
                    handleSubRowEditBlur={editing.handleSubRowEditBlur}
                    handleToggleRowExpand={handleToggleRowExpand}
                    handleRowSave={save.handleRowSave}
                    handleRowCancel={save.handleRowCancel}
                    externalEditControl={internalSchema.features.editing.externalControl}
                    stateActions={{ toggleGroup: stateActions.toggleGroup }}
                    rowStyleFn={resolvedRowStyleFn}
                    subRowOverflowCols={subRowOverflowCols}
                  />
                  {internalSchema.features.footer != null && (
                    <FooterRenderer
                      config={internalSchema.features.footer}
                      visibleColumns={columns.visibleColumns}
                      data={displayData as ReadonlyArray<Record<string, unknown>>}
                      footerData={footerData}
                      showCheckbox={showSelectionColumn}
                      subRowOverflowCols={subRowOverflowCols}
                      showActionsColumn={showActionsColumn}
                    />
                  )}
                </React.Fragment>
              )}
            </DataTable.Content>

            {/* ---- INFINITE SCROLL STATUS (pagination-like, always visible) ---- */}
            {infiniteScrollStatusBar}

            {/* ---- PAGINATION ---- */}
            {(paginationConfig.mode === 'pagination' || paginationConfig.mode === 'clientSide') &&
              pipeline.paginatedData.length > 0 && (
                <PaginationRenderer
                  mode={paginationConfig.mode}
                  hasData={pipeline.paginatedData.length > 0}
                  pageIndex={tableState.pagination.pageIndex}
                  pageSize={tableState.pagination.pageSize}
                  totalCount={effectiveTotalCount}
                  visibleCount={pipeline.paginatedData.length}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
          </DataTable.Root>

          {/* ---- DIALOGS ---- */}
          <TableDialogs
            pendingConfirm={pendingConfirm}
            setPendingConfirm={setPendingConfirm}
            pendingSaveConfirm={save.pendingSaveConfirm}
            pendingBackgroundJob={pendingBackgroundJob}
            setPendingBackgroundJob={setPendingBackgroundJob}
            effectiveTotalCount={effectiveTotalCount}
            confirmRenderer={confirmRenderer}
            defaultNavigate={defaultNavigate}
          />
        </div>
      </V2TableInternalContext.Provider>
    );
  }
);

// ---------------------------------------------------------------------------
// Public export (with error boundary)
// ---------------------------------------------------------------------------

const DynamicTableV2 = forwardRef<DynamicTableV2Handle, DynamicTableV2Props>(
  function DynamicTableV2(props, ref) {
    if (isMultiSectionSchema(props.schema)) {
      return (
        <DynamicTableV2ErrorBoundary key='multi-section'>
          <MultiSectionRenderer {...props} DynamicTableV2Component={DynamicTableV2} />
        </DynamicTableV2ErrorBoundary>
      );
    }

    const modeKey = props.__unstable_useV1Renderer ? 'v1' : 'v2';

    return (
      <DynamicTableV2ErrorBoundary key={modeKey}>
        {props.__unstable_useV1Renderer ? (
          <V1RendererBridge {...props} />
        ) : (
          <DynamicTableV2Inner {...props} ref={ref} />
        )}
      </DynamicTableV2ErrorBoundary>
    );
  }
);

DynamicTableV2.displayName = 'DynamicTableV2';

export default DynamicTableV2;
export { DynamicTableV2 };
